import { css, cx } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowDownFromDottedLineIcon, ArrowsToDottedLineIcon, ArrowUpFromDottedLineIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { createElement, useCallback } from "react";
import type { DecorationProps, HunkData } from "react-diff-view";
import { Decoration, getCollapsedLinesCountBetween } from "react-diff-view";
const MAX_LINES_UNFOLD = 20;
const ICON_TYPE_MAPPING = {
    up: ArrowUpFromDottedLineIcon,
    down: ArrowDownFromDottedLineIcon,
    out: ArrowsToDottedLineIcon,
};
const TOOLTIP_TEXT_MAPPING = {
    up: "Expand Up",
    down: "Expand Down",
    out: "Expand All",
};
interface UnfoldPropsBase {
    onExpand: (start: number, end: number) => void;
    content?: string;
}
interface UnfoldInternalProps extends Omit<DecorationProps, "children">, UnfoldPropsBase {
    start: number;
    end: number;
    direction: "up" | "down" | "out";
}
interface UnfoldInwardsInternalProps extends Omit<DecorationProps, "children">, UnfoldPropsBase {
    topStart: number;
    topEnd: number;
    bottomStart: number;
    bottomEnd: number;
}
const styles = {
    normalContent: css({
        paddingLeft: space[16],
        backgroundColor: themeTokens.color.background.secondary.default,
        height: "28px",
        display: "flex",
        alignItems: "center",
    }),
    gutterBackground: css({
        backgroundColor: themeTokens.color.background.tertiary,
        padding: 0,
    }),
    normalGutterContent: css({
        cursor: "pointer",
        height: "28px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ":hover": {
            // There is currently no theme token for background.tertiary.hover
            backgroundColor: themeTokens.color.background.info,
        },
    }),
    fullContentOverride: css({
        height: "40px",
    }),
    semiGutterContentOverride: css({
        height: "20px",
    }),
};
const UnfoldInternal = ({ start, end, direction, onExpand, ...props }: UnfoldInternalProps) => {
    const expand = useCallback(() => onExpand(start, end), [onExpand, start, end]);
    return (<Decoration {...props} gutterClassName={styles.gutterBackground}>
            <Tooltip content={TOOLTIP_TEXT_MAPPING[direction]} display={"block"}>
                <div className={styles.normalGutterContent} onClick={expand}>
                    {createElement(ICON_TYPE_MAPPING[direction], { size: 20 })}
                </div>
            </Tooltip>
            <div className={styles.normalContent}>
                <>{props.content}</>
            </div>
        </Decoration>);
};
const UnfoldInwardsInternal = ({ topStart, topEnd, bottomStart, bottomEnd, onExpand, ...props }: UnfoldInwardsInternalProps) => {
    const expandDown = useCallback(() => onExpand(topStart, topEnd), [onExpand, topStart, topEnd]);
    const expandUp = useCallback(() => onExpand(bottomStart, bottomEnd), [onExpand, bottomStart, bottomEnd]);
    return (<>
            <Decoration {...props} gutterClassName={styles.gutterBackground}>
                <div>
                    <Tooltip content={TOOLTIP_TEXT_MAPPING["down"]} display={"block"}>
                        <div className={cx(styles.normalGutterContent, styles.semiGutterContentOverride)} onClick={expandDown}>
                            {createElement(ArrowDownFromDottedLineIcon, { size: 20 })}
                        </div>
                    </Tooltip>
                    <Tooltip content={TOOLTIP_TEXT_MAPPING["up"]} display={"block"}>
                        <div className={cx(styles.normalGutterContent, styles.semiGutterContentOverride)} onClick={expandUp}>
                            {createElement(ArrowUpFromDottedLineIcon, { size: 20 })}
                        </div>
                    </Tooltip>
                </div>
                <div className={cx(styles.normalContent, styles.fullContentOverride)}>
                    <>{props.content}</>
                </div>
            </Decoration>
        </>);
};
export interface UnfoldCollapsedProps {
    previousHunk: HunkData;
    currentHunk?: HunkData;
    linesCount: number;
    onExpand: (start: number, end: number) => void;
}
export default ({ previousHunk, currentHunk, linesCount, onExpand }: UnfoldCollapsedProps) => {
    if (!currentHunk) {
        const nextStart = previousHunk.oldStart + previousHunk.oldLines;
        const collapsedLines = linesCount - nextStart + 1;
        if (collapsedLines <= 0) {
            return null;
        }
        const end = Math.min(nextStart + MAX_LINES_UNFOLD, linesCount + 1);
        return <UnfoldInternal direction="down" start={nextStart} end={end} onExpand={onExpand}/>;
    }
    const collapsedLines = getCollapsedLinesCountBetween(previousHunk, currentHunk);
    if (!previousHunk) {
        if (!collapsedLines) {
            return null;
        }
        const start = Math.max(currentHunk.oldStart - MAX_LINES_UNFOLD, 1);
        return <UnfoldInternal direction="up" start={start} end={currentHunk.oldStart} onExpand={onExpand} content={currentHunk.content}/>;
    }
    const collapsedStart = previousHunk.oldStart + previousHunk.oldLines;
    const collapsedEnd = currentHunk.oldStart;
    if (collapsedLines < MAX_LINES_UNFOLD) {
        return <UnfoldInternal direction="out" start={collapsedStart} end={collapsedEnd} onExpand={onExpand} content={currentHunk.content}/>;
    }
    return <UnfoldInwardsInternal topStart={collapsedStart} topEnd={collapsedStart + MAX_LINES_UNFOLD} bottomStart={collapsedEnd - MAX_LINES_UNFOLD} bottomEnd={collapsedEnd} onExpand={onExpand} content={currentHunk.content}/>;
};
